// ============================================================
// Platform Control · Privacy — scrubbing, visibility boundaries,
// and sensitive-data handling. Reflects real implementation truth
// from Sentry privacy config and OLIVIUM behavior.
// ============================================================

import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section, ActionButton, ActionRow } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS } from '../../../shell/styles'

export default function PrivacyView() {
  const navigate = useNavigate()

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · PRIVACY"
      title="Privacy"
      description="Scrubbing, visibility boundaries, and sensitive-data handling."
      actions={
        <button
          type="button"
          onClick={() => navigate('/square/olivium/feed')}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: 'transparent',
            color: SHELL_TOKENS.ink,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        >
          Open Audit Policy →
        </button>
      }
    >
      <Section label="TELEMETRY SCRUBBING">
        <Card>
          <Row label="sendDefaultPii" value="false" hint="Sentry default — no PII auto-collection" />
          <Row label="Auth headers" value="scrubbed" hint="Authorization, Cookie, x-api-key stripped from events" />
          <Row label="Supabase tokens" value="scrubbed" hint="sb-*, apikey params removed" />
          <Row label="Query strings" value="scrubbed" hint="sensitive params replaced with [filtered]" />
          <Row label="Session replay" value="masked" hint="all text, inputs, and media masked by default" />
          <Row label="Breadcrumbs" value="scrubbed" hint="auth headers + cookies stripped from XHR breadcrumbs" />
        </Card>
      </Section>

      <Section label="OPERATOR VISIBILITY">
        <Card>
          <Row label="OLIVIUM audit trail" value="full access" hint="operators see all audit events" />
          <Row label="Session governance" value="full access" hint="operators can view and revoke sessions" />
          <Row label="Device trust" value="visible" hint="fingerprint + trust level per device" />
          <Row label="Alert delivery" value="visible" hint="operators see alert actions and delivery status" />
        </Card>
      </Section>

      <Section label="CLIENT VISIBILITY">
        <Card>
          <Row label="Audit events" value="scoped" hint="RLS limits to own-org events" />
          <Row label="Sessions" value="own only" hint="users see their own sessions" />
          <Row label="API keys" value="none" hint="key table is service-role only" />
          <Row label="Admin routes" value="gated" hint="minLevel: 80 required for admin actions" />
        </Card>
      </Section>

      <Section label="SECRETS HANDLING">
        <Card>
          <Row label="Service role key" value="server-side only" hint="never in browser bundle — enforced by import gating" />
          <Row label="Redis URL" value="server-side only" hint="behind subpath import @square/core/redis" />
          <Row label="Provider tokens" value="server-side only" hint="Slack, Resend, Vercel, Gamma, Gladia" />
          <Row label="Health endpoints" value="alias names only" hint="sentryHealth() reports booleans, never values" />
        </Card>
      </Section>

      <Section label="RETENTION">
        <Card>
          <Row label="Audit trail" value="indefinite" hint="hash-chained, immutable, no TTL" />
          <Row label="Sessions" value="stale-expired" hint="olv_expire_stale_sessions() cron" />
          <Row label="Sentry events" value="per plan" hint="controlled by Sentry retention settings" />
          <Row label="Data residency" value="not configured" hint="Supabase region: default" />
        </Card>
      </Section>

      <Section label="OPERATOR ACTIONS">
        <ActionRow>
          <ActionButton onClick={() => navigate('/square/olivium/feed')}>
            View Audit Trail
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/olivium/sessions')}>
            View Sessions
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/control/security')}>
            Security Posture
          </ActionButton>
          <ActionButton onClick={() => navigate('/square/__diag?debug=1')}>
            Sentry Diagnostics
          </ActionButton>
        </ActionRow>
      </Section>
    </ViewShell>
  )
}
